import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, AlertTriangle, ArrowRight, Calendar, BarChart2, Award, TrendingUp } from 'lucide-react';
// Exam data types
interface Exam {
  id: number;
  title: string;
  questionCount: number;
  duration: number; // in minutes
  completed: boolean;
  date: string;
}
// Completed exam result type
interface ExamResult {
  id: number;
  title: string;
  date: string;
  totalNet: number;
  totalCorrect: number;
  totalIncorrect: number;
  totalEmpty: number;
  lgsPuan?: number; // MSP score
  siralamaTahmini?: number; // Approximate ranking
}
export function OnlineExams() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'exams' | 'results'>(location.state?.activeTab || 'exams');
  const [exams, setExams] = useState<Exam[]>([]);
  const [completedExams, setCompletedExams] = useState<ExamResult[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  // Load exams data from local storage or initialize with defaults
  useEffect(() => {
    setLoading(true);
    // Check local storage for active exams
    const storedActiveExams = localStorage.getItem('activeExams');
    if (storedActiveExams) {
      setExams(JSON.parse(storedActiveExams));
    } else {
      // Initial active exams data
      const initialExams = [{
        id: 1,
        title: 'LGS Genel Deneme Sınavı - 1',
        questionCount: 90,
        duration: 155,
        completed: false,
        date: '2023-10-25'
      }, {
        id: 2,
        title: 'LGS Genel Deneme Sınavı - 2',
        questionCount: 90,
        duration: 155,
        completed: false,
        date: '2023-11-01'
      }, {
        id: 3,
        title: 'LGS Genel Deneme Sınavı - 3',
        questionCount: 90,
        duration: 155,
        completed: false,
        date: '2023-11-10'
      }, {
        id: 4,
        title: 'LGS Genel Deneme Sınavı - 4',
        questionCount: 90,
        duration: 155,
        completed: false,
        date: '2023-11-25'
      }];
      setExams(initialExams);
      localStorage.setItem('activeExams', JSON.stringify(initialExams));
    }
    // Check local storage for completed exams
    const storedCompletedExams = localStorage.getItem('completedExams');
    if (storedCompletedExams) {
      setCompletedExams(JSON.parse(storedCompletedExams));
    } else {
      // No need to initialize with defaults - completed exams will be populated when exams are completed
      localStorage.setItem('completedExams', JSON.stringify([]));
    }
    setLoading(false);
  }, []);
  // Function to view exam result details
  const viewExamResult = (examId: number) => {
    navigate(`/online-denemeler/${examId}/sonuc`, {
      state: {
        fromStorage: true
      }
    });
  };
  return <div className="space-y-6" data-id="element-2590">
      <h1 className="text-3xl font-bold" data-id="element-2591">Online Denemeler</h1>
      {/* Tabbed navigation */}
      <div className="border-b border-gray-800 mb-6" data-id="element-2592">
        <div className="flex space-x-8" data-id="element-2593">
          <button onClick={() => setActiveTab('exams')} className={`py-3 px-1 flex items-center gap-2 relative ${activeTab === 'exams' ? 'text-purple-400 font-medium' : 'text-gray-400 hover:text-gray-300'}`} data-id="element-2594">
            <Calendar className="w-4 h-4" data-id="element-2595" />
            Denemeler
            {activeTab === 'exams' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500" data-id="element-2596"></div>}
          </button>
          <button onClick={() => setActiveTab('results')} className={`py-3 px-1 flex items-center gap-2 relative ${activeTab === 'results' ? 'text-purple-400 font-medium' : 'text-gray-400 hover:text-gray-300'}`} data-id="element-2597">
            <BarChart2 className="w-4 h-4" data-id="element-2598" />
            Sonuçlar
            {activeTab === 'results' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500" data-id="element-2599"></div>}
          </button>
        </div>
      </div>
      {activeTab === 'exams' && <>
          {/* Exam description */}
          <div className="border border-purple-500 rounded-xl overflow-hidden mb-4" data-id="element-2600">
            <div className="p-6" data-id="element-2601">
              <h2 className="text-xl font-bold mb-3" data-id="element-2602">
                LGS Deneme Sınavı Formatı
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2603">
                <div className="space-y-2" data-id="element-2604">
                  <p className="text-gray-300" data-id="element-2605">
                    <strong data-id="element-2606">Toplam Soru:</strong> 90 soru
                  </p>
                  <p className="text-gray-300" data-id="element-2607">
                    <strong data-id="element-2608">Toplam Süre:</strong> 155 dakika (Sözel: 75 dk,
                    Sayısal: 80 dk)
                  </p>
                </div>
                <div className="space-y-2" data-id="element-2609">
                  <p className="text-gray-300" data-id="element-2610">
                    <strong data-id="element-2611">Sözel Bölüm:</strong> Türkçe (20), T.C. İnkılap
                    (10), Din Kültürü (10), İngilizce (10)
                  </p>
                  <p className="text-gray-300" data-id="element-2612">
                    <strong data-id="element-2613">Sayısal Bölüm:</strong> Matematik (20), Fen
                    Bilimleri (20)
                  </p>
                </div>
              </div>
            </div>
          </div>
          {loading ? <div className="flex items-center justify-center h-64" data-id="element-2614">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500" data-id="element-2615"></div>
            </div> : exams.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2616">
              {exams.map(exam => <div key={exam.id} className="border border-purple-500 rounded-xl overflow-hidden" data-id="element-2617">
                  <div className="p-6 h-full flex flex-col" data-id="element-2618">
                    <h3 className="text-xl font-bold mb-2" data-id="element-2619">{exam.title}</h3>
                    <div className="space-y-2 mb-4" data-id="element-2620">
                      <div className="flex items-center gap-2 text-gray-300" data-id="element-2621">
                        <Clock className="w-4 h-4" data-id="element-2622" />
                        <span data-id="element-2623">Toplam Süre: {exam.duration} Dakika</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300" data-id="element-2624">
                        <CheckCircle className="w-4 h-4" data-id="element-2625" />
                        <span data-id="element-2626">Toplam Soru: {exam.questionCount}</span>
                      </div>
                    </div>
                    <div className="mt-auto" data-id="element-2627">
                      <Link to={`/online-denemeler/${exam.id}/coz`} className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-colors" data-id="element-2628">
                        Denemeye Başla
                        <ArrowRight className="w-5 h-5" data-id="element-2629" />
                      </Link>
                    </div>
                  </div>
                </div>)}
            </div> : <div className="border border-purple-500 rounded-xl overflow-hidden" data-id="element-2630">
              <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-2631">
                <AlertTriangle className="w-16 h-16 text-yellow-500 mb-4" data-id="element-2632" />
                <h3 className="text-xl font-medium mb-2" data-id="element-2633">
                  Şu an size atanmış bir deneme sınavı bulunmamaktadır.
                </h3>
                <p className="text-gray-400" data-id="element-2634">
                  Öğretmenleriniz tarafından atanan denemeler burada
                  listelenecektir.
                </p>
              </div>
            </div>}
        </>}
      {/* Results tab content */}
      {activeTab === 'results' && <>
          <h2 className="text-xl font-bold mb-4" data-id="element-2635">Tamamlanan Denemeler</h2>
          {loading ? <div className="flex items-center justify-center h-64" data-id="element-2636">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500" data-id="element-2637"></div>
            </div> : completedExams.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2638">
              {completedExams.map(exam => <div key={exam.id} className="border border-purple-500 rounded-xl overflow-hidden" data-id="element-2639">
                  <div className="p-6 h-full flex flex-col" data-id="element-2640">
                    <h3 className="text-xl font-bold mb-2" data-id="element-2641">{exam.title}</h3>
                    <div className="space-y-2 mb-4" data-id="element-2642">
                      <div className="flex items-center gap-2 text-gray-300" data-id="element-2643">
                        <Calendar className="w-4 h-4" data-id="element-2644" />
                        <span data-id="element-2645">
                          Tarih:{' '}
                          {new Date(exam.date).toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-purple-400" data-id="element-2646">
                        <TrendingUp className="w-4 h-4" data-id="element-2647" />
                        <span data-id="element-2648">Toplam Net: {exam.totalNet.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-yellow-400" data-id="element-2649">
                        <Award className="w-4 h-4" data-id="element-2650" />
                        <span data-id="element-2651">LGS Puanı: {exam.lgsPuan?.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="mt-auto" data-id="element-2652">
                      <button onClick={() => viewExamResult(exam.id)} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-colors" data-id="element-2653">
                        Sonucu Gör
                        <ArrowRight className="w-5 h-5" data-id="element-2654" />
                      </button>
                    </div>
                  </div>
                </div>)}
            </div> : <div className="border border-purple-500 rounded-xl overflow-hidden" data-id="element-2655">
              <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-2656">
                <AlertTriangle className="w-16 h-16 text-yellow-500 mb-4" data-id="element-2657" />
                <h3 className="text-xl font-medium mb-2" data-id="element-2658">
                  Henüz tamamlanmış deneme sınavınız bulunmamaktadır.
                </h3>
                <p className="text-gray-400" data-id="element-2659">
                  Tamamladığınız denemeler burada listelenecektir.
                </p>
              </div>
            </div>}
        </>}
    </div>;
}